import * as Sentry from '@sentry/react';
import { Chat } from 'dragonmouse-shared/interfaces';
import { VITE_FORGE_URL } from '../constants';
import {
	resolveOptimisticallyUpdatedChats,
	setSceneLoadError,
	updateChats,
} from '../redux/db/actions';
import store from '../redux/store';
import { PollingStream } from './PollingStream';

export class ChatPollingStream extends PollingStream<Chat> {
	constructor(activeSceneId: string) {
		const url = VITE_FORGE_URL + `/poll/scene/${activeSceneId}/chats`;

		const onError = (error: Error) => {
			if (error.message === 'Access denied') {
				store.dispatch(setSceneLoadError('You do not have access to this project'));
				store.dispatch(updateChats([]));
			} else {
				console.error(error);
			}
		};

		super(url, onError, 'activeScene.chats');
	}

	extractData(response: any): Chat[] {
		return response.chats || [];
	}

	onData(chats: Chat[]) {
		// Resolve optimistic writes based on writeId from the database
		const writeIds = chats
			.map((chat) => chat.writeId)
			.filter((writeId): writeId is string => !!writeId);

		if (writeIds.length > 0) {
			store.dispatch(resolveOptimisticallyUpdatedChats(writeIds));
		}

		// The backend already returns Chat objects with proper camelCase field names
		// (thanks to postgres.camel transform)
		// We just need to convert date strings to Date objects
		const processedChats: Chat[] = chats.map((chat) => ({
			...chat,
			createdAt: new Date(chat.createdAt),
			updatedAt: new Date(chat.updatedAt),
		}));

		store.dispatch(updateChats(processedChats));

		// Detect if optimistic updates have been applied
		// Get all write IDs that were resolved by the server
		const resolvedWriteIds = new Set(writeIds);

		// Check for any optimistic writes that weren't resolved
		const state = store.getState();
		const unresolvedWrites = state.db.optimisticChatWrites.filter(
			(write) => !resolvedWriteIds.has(write.id)
		);

		if (unresolvedWrites.length > 0) {
			const now = Date.now();
			unresolvedWrites.forEach((write) => {
				if (write.value.createdAt instanceof Date) {
					const age = now - write.value.createdAt.getTime();
					if (age > 2500) {
						const message =
							`[PollingStream] Optimistic ${write.operation} for chat ${write.value.id} ` +
							`not resolved after ${age}ms (writeId: ${write.id})`;
						console.warn(message);
						Sentry.captureMessage(message, 'warning');
					}
				}
			});
		}
	}
}
